import { type IMap, type ILatLon, type IMapProjection, type IMapLibrary, type IMapProps, type ILatLonBounds, type EMapShape, type IMapState, type IMapDrawCallback, type IMapStateCallback, type IMapStateEvent, type IMapDrawEvent, type IMapPointerCallback } from '@/Root/Types/MapTypes'
import { type IDrawTool, type IDrawToolProps, type IDrawToolPropsOptions } from '@/Root/Types/ToolTypes'
import { type ITileLayerProps, type IWMSLayerProps, type ILayerProps, type IGeoJSONLayerProps, type IGeoJSONLayerImplementation, type ILayerLibrary, type ILayerImplementation } from '@/Root/Types/LayerTypes'
import { baseLayersMapped } from '@/Root/baseLayers'
import LatLonBounds from './LatLonBounds'
import { EventManager, EventWithData, type IEventCallback } from '@axdspub/axiom-ui-utilities'

class MapStateEvent extends EventWithData implements IMapStateEvent {}

class MapDrawEvent extends EventWithData implements IMapDrawEvent {}

class MapRoot extends EventManager implements IMap {
  protected mapElement?: HTMLElement | undefined
  protected runtime?: any | undefined
  element?: HTMLElement | undefined
  center?: ILatLon
  zoom?: number
  baseLayerKey?: string | undefined
  baseLayer?: ILayerProps | undefined
  layers?: ILayerProps[] | undefined
  tools?: {
    draw?: IDrawToolPropsOptions
    polygonQuery?: object | boolean
    projectionChanger?: object | boolean
    timeControl?: object | boolean
  }

  toolInstances: {
    draw?: IDrawTool | undefined
  }

  onMapViewChangeCallBack?: (mapStateEvent: IMapStateEvent) => void
  onMapMoveStartCallBack?: (mapStateEvent: IMapStateEvent) => void

  projection?: IMapProjection | undefined
  mapLibrary?: IMapLibrary | undefined
  mapLibraryKey?: string
  map: any

  constructor (props: IMapProps) {
    super()
    this.runtime = {}
    this.mapLibraryKey = props.mapLibraryKey === undefined ? 'leaflet' : props.mapLibraryKey
    if (props.mapLibrary != null) {
      this.mapLibrary = props.mapLibrary
    }
    if (props.element != null) {
      this.element = props.element
    }
    if (props.center != null) {
      this.center = props.center
    }
    if (props.zoom !== undefined) {
      this.zoom = props.zoom
    }
    if (props.baseLayerKey !== undefined) {
      this.baseLayerKey = props.baseLayerKey
    }
    if (props.baseLayer != null) {
      this.baseLayer = props.baseLayer
    }
    if (props.layers !== undefined) {
      this.layers = props.layers
    } else {
      this.layers = []
    }

    if (props.tools != null) {
      this.tools = props.tools
    }
    if (props.projection != null) {
      this.projection = props.projection
    }

    this.toolInstances = {}

    if (props.onMapLoaded != null) {
      this.onMapLoaded(props.onMapLoaded)
    }
    if (props.onMapViewChange !== null) {
      this.onMapViewChangeCallBack = props.onMapViewChange
    }
    if (props.onMapMoveStart !== null) {
      this.onMapMoveStartCallBack = props.onMapMoveStart
    }

    if (this.element != null) {
      this.initMap()
    }
  }

  public reloadLayers (layers: ILayerProps[]): void {
    if (this.layers === undefined) return
    this.removeLayers(this.layers)
    this.addLayers(layers)
  }

  /**
   *
   *   (eventType: string, callback: IEventCallback): void
  (eventType: 'drawstart', callback: IMapDrawCallback): void
  (eventType: 'drawend', callback: IMapDrawCallback): void
  (eventType: 'drawupdate', callback: IMapDrawCallback): void
  (eventType: 'drawprogress', callback: IMapDrawCallback): void
  (eventType: 'loaded', callback: IMapStateCallback): void
  (eventType: 'moveend', callback: IMapStateCallback): void
  (eventType: 'pointermove', callback: IMapPointerMoveCallback): void
  (eventType: 'pointerselect', callback: IMapPointerSelectCallback): void
   */

  protected getDefaultLatLon (): ILatLon {
    return { lat: 0, lon: 0 }
  }

  public pointToLatLon (pt: { x: number, y: number }): ILatLon {
    console.log(`pointToLatLon not yet implemented on ${this.getMapLibraryName()}`)
    return this.getDefaultLatLon()
  }

  public mapPointToWindowPoint (point: { x: number, y: number }): { x: number, y: number } {
    const rect = this.element !== undefined ? this.element.getBoundingClientRect() : { left: 0, top: 0 }
    return {
      x: point.x + rect.left,
      y: point.y + rect.top
    }
  }

  protected dispatchMapLoadedEvent (): void {
    this.dispatchEventWithData('loaded')
  }

  protected dispatchMapMoveStartEvent (e?: Event): void {
    this.dispatchEventWithData('movestart', { mapState: this.getMapState() }, e)
  }

  protected dispatchMapMoveEvent (e?: Event): void {
    this.dispatchEventWithData('moveend', { mapState: this.getMapState() }, e)
  }

  protected dispatchMapClickEvent (e?: Event): void {
    this.dispatchEventWithData('mapclick')
  }

  protected dispatchPointerMove (point?: { x: number, y: number }, latlon?: ILatLon, extraData?: object, nativeEvent?: Event): void {
    const data = point !== undefined
      ? {
          windowPoint: this.mapPointToWindowPoint(point),
          point,
          latlon: this.pointToLatLon(point),
          ...extraData
        }
      : undefined

    this.dispatchEventWithData('pointermove', data, nativeEvent)
  }

  protected dispatchPointerSelect (point: { x: number, y: number }, latlon?: ILatLon, nativeEvent?: Event): void {
    const windowPoint = this.mapPointToWindowPoint(point)
    this.dispatchEventWithData('pointerselect', { windowPoint, point, latlon: this.pointToLatLon(point) }, nativeEvent)
  }

  public onMapLoaded (callback: IMapStateCallback): void {
    this.addEventListener('loaded', callback)
    if (this.map !== undefined) {
      callback(new MapStateEvent('loaded', {
        data: {
          mapState: this.getMapState()
        }
      }))
    }
  }

  public onMapMoveStart (callback: IMapStateCallback): void {
    console.log('onmap')
    this.addEventListener('movestart', callback)
  }

  public onMapViewChange (callback: IMapStateCallback): void {
    this.addEventListener('moveend', callback)
  }

  public onPointerMove (callback: IMapPointerCallback): void {
    this.addEventListener('pointermove', callback)
  }

  public onPointerSelect (callback: IMapPointerCallback): void {
    this.addEventListener('pointerselect', callback)
  }

  public on (eventType: string, callback: IEventCallback): void {
    this.addEventListener(eventType, callback)
  }

  protected getDefaultMapState (): IMapState {
    return {
      center: {
        lat: 0,
        lon: 0
      },
      zoom: 0,
      bounds: new LatLonBounds({
        ne: {
          lat: 90,
          lon: -180
        },
        sw: {
          lat: -90,
          lon: 180
        }
      })

    }
  }

  public getMapState (): IMapState {
    const bounds = this.getMapBounds()
    const center = this.getMapCenter()
    const zoom = this.getMapZoom()
    if (bounds !== undefined && center !== undefined && zoom !== undefined) {
      return {
        zoom,
        center,
        bounds

      }
    } else {
      return this.getDefaultMapState()
    }
  }

  public setBaseLayerKey (baseLayerKey: string): void {
    if (baseLayersMapped[baseLayerKey] !== undefined) {
      this.baseLayerKey = baseLayerKey
      this.setBaseLayer({ ...baseLayersMapped[baseLayerKey] })
    }
  }

  public getBaseLayerKey (): string | undefined {
    return this.baseLayerKey
  }

  public setBaseLayer (layer: ILayerProps): void {
    if ((this.baseLayer != null) && this.map !== undefined) {
      this.removeLayerFromMap(this.baseLayer)
    }
    this.baseLayer = layer
    if (this.map !== undefined) {
      this.setMapBaseLayer(layer)
    }
  }

  // TODO: Handle case where there are multiple layers of the same type.
  // Here we are finding the layer by ILayerProps. We should be finding it by a unique id.
  public getLayer (layerIdentifier: ILayerProps | string): ILayerProps | undefined {
    if (typeof layerIdentifier === 'string') {
      console.log(this.layers)
      return this.layers?.find(l => l.id === layerIdentifier)
    }
    return this.layers?.find(l => l.id === layerIdentifier.id)
  }

  public hasLayer (layer: ILayerProps | string): boolean {
    if (typeof layer === 'string') {
      return this.layers?.find(l => l.id === layer) !== undefined
    }
    return ((this.layers?.find(l => l.id === layer.id)) !== undefined)
  }

  public addLayer (layer: ILayerProps): void {
    if (!this.hasLayer(layer)) {
      this.layers?.push(layer)
    }
    if (this.map !== undefined && (layer.layer === undefined || layer.layer === null)) {
      layer.creating = true
      this.addLayerToMap(layer).then(() => {
        layer.creating = false
      }).catch(e => {
        layer.error = true
        layer.creating = false
        console.log('Error creating layer')
      })
    }
  };

  public addLayers (layers: ILayerProps[]): void {
    layers.forEach(layer => {
      this.addLayer(layer)
    })
  };

  public removeLayer (layerIdentifier: ILayerProps | string): void {
    console.log('removeLayer')
    const layer = this.getLayer(layerIdentifier)
    if (layer !== undefined) {
      if (this.layers != null) {
        if (this.layers.includes(layer)) {
          console.log('removing from array')
          this.layers.splice(this.layers.indexOf(layer), 1)
        }
        if (this.map !== undefined && (layer.layer !== undefined && layer.layer !== null)) {
          console.log('removing from map')
          this.removeLayerFromMap(layer)
        } else {
          console.log(this.map)
          console.log(layer)
        }
      }
    }
  };

  public removeLayers (layers: Array<ILayerProps | string>): void {
    layers.forEach(layer => {
      this.removeLayer(layer)
    })
  };

  public setZoom (zoom: number): void {
    this.zoom = zoom
    if (this.map !== undefined) {
      this.setMapZoom(zoom)
    }
  };

  public getZoom (): number | undefined {
    return this.zoom
  }

  public setCenter (center: ILatLon): void {
    this.center = center
    if (this.map !== undefined) {
      this.setMapCenter(center)
    }
  }

  public getCenter (): ILatLon | undefined {
    return this.center
  }

  public setBounds (bounds: ILatLonBounds): void {
    if (this.map !== undefined) {
      this.setMapBounds(bounds)
    }
  }

  public getBounds (): ILatLonBounds | undefined | null {
    if (this.map !== undefined) {
      return this.getMapBounds()
    }
    return null
  }

  public setProjection (projection: IMapProjection): void {
    this.projection = projection
    if (this.map !== undefined) {
      this.setMapProjection(projection)
    }
  }

  public getProjection (): IMapProjection | undefined {
    return this.projection
  }

  public setElement (element: HTMLElement): void {
    this.element = element
    if (this.map !== undefined) {
      this.setMapElement(element)
    }
  }

  public getElement (): HTMLElement | undefined | null {
    return this.element
  }

  public setMapBaseLayer (layer: ILayerProps): void {
    layer.creating = true
    this.addLayerToMap(layer).then(() => {
      layer.creating = false
    }).catch(e => {
      layer.creating = false
      layer.error = true
      console.log('Error creating base layer')
    })
    // console.log(`setMapBaseLayer no implemented for ${this.getMapLibraryName()}`)
  }

  private getMapLibraryName (): string {
    if (this.mapLibraryKey !== undefined) {
      return this.mapLibraryKey
    }
    return '(no library key)'
  }

  public createGeojsonLayer (layer: IGeoJSONLayerProps): IGeoJSONLayerImplementation | undefined {
    console.log(`createGeojsonLayer not yet implemented on ${this.getMapLibraryName()}`)
    return undefined
  }

  protected async createLayer (layer: ILayerProps): Promise<ILayerLibrary | undefined> {
    const layerTypeMap = this.mapLibrary?.LayerTypes !== undefined ? Object.fromEntries(this.mapLibrary.LayerTypes.map(l => [l.type, l])) : {}
    if (layer.libraryPath !== undefined) {
      console.log('LOAD IT')
    } else if (layerTypeMap[layer.type] !== undefined) {
      return layerTypeMap[layer.type]
    }
    return undefined
  }

  protected addLayerImplementationToMap (layerImplementation: ILayerImplementation): void {
    console.log(`addLayerImplementationToMap not yet implemented on ${this.getMapLibraryName()}. Layers will not be added to your map until you do`)
  }

  public async addLayerToMap (layer: ILayerProps): Promise<void> {
    const layerLibrary = await this.createLayer(layer)
    if (layerLibrary !== undefined) {
      const Implementation = layerLibrary.Implementation as any
      const layerImplementation: ILayerImplementation = new Implementation(layer, this)
      this.addLayerImplementationToMap(layerImplementation)
      layer.implementation = layerImplementation
    } else {
      /* const type = layer.type
      if (type === 'tile') {
        this.addTileLayerToMap(layer as ITileLayerProps)
      } else if (type === 'wms') {
        this.addWMSLayerToMap(layer as IWMSLayerProps)
      } else if (type === 'geoJson') {
        this.addGeoJsonLayerToMap(layer as IGeoJSONLayerProps)
      } else {
        console.log(`layer type not supported by ${this.getMapLibraryName()}`)
      } */
      console.log(`layer type ${layer.type} not supported by ${this.getMapLibraryName()}`)
    }
  }

  public addTileLayerToMap (layer: ITileLayerProps): void {
    console.log(`addTileLayerToMap no implemented for ${this.getMapLibraryName()}`)
  }

  public addWMSLayerToMap (layer: IWMSLayerProps): void {
    console.log(`addWMSLayerToMap no implemented for ${this.getMapLibraryName()}`)
  }

  public addGeoJsonLayerToMap (layer: IGeoJSONLayerProps): void {
    console.log(`addGeoJsonLayerToMap no implemented for ${this.getMapLibraryName()}`)
  }

  public removeLayerFromMap (layer: ILayerProps): void {
    console.log(`removeLayerFromMap not implemented for ${this.getMapLibraryName()}`)
  }

  public setMapZoom (zoom: number): void {
    console.log(`setMapZoom not implemented for ${this.getMapLibraryName()}`)
  }

  public getMapZoom (): number | undefined {
    console.log(`getMapZoom not implemented for ${this.getMapLibraryName()}`)
    return 0
  }

  public setMapCenter (center: ILatLon): void {
    console.log(`setMapCenter not implemented for ${this.getMapLibraryName()}`)
  }

  public getMapCenter (): ILatLon | undefined {
    console.log(`getMapCenter not implemented for ${this.getMapLibraryName()}`)
    return undefined
  }

  public setMapBounds (bounds: ILatLonBounds): void {
    console.log(`setMapBounds not implemented for ${this.getMapLibraryName()}`)
  }

  public getMapBounds (): ILatLonBounds | undefined {
    console.log(`getMapBounds not implemented for ${this.getMapLibraryName()}`)
    return undefined
  }

  public setMapProjection (projection: IMapProjection): void {
    console.log(`setMapProjection not implemented for ${this.getMapLibraryName()}`)
  }

  public setMapElement (element: HTMLElement): void {
    console.log(`setMapProjection not implemented for ${this.getMapLibraryName()}`)
  }

  public getIDrawToolInstance (): IDrawTool | undefined {
    if (this.toolInstances.draw !== undefined) {
      return this.toolInstances.draw
    }
    return undefined
  }

  public getDrawGeojson (): GeoJSON.FeatureCollection | undefined {
    const drawToolInstance = this.getIDrawToolInstance()
    if (drawToolInstance !== undefined) {
      return drawToolInstance.getGeojson()
    }
    return undefined
  }

  public setDrawGeojson (geoJSON: GeoJSON.FeatureCollection): void {
    const drawToolInstance = this.getIDrawToolInstance()
    if (drawToolInstance !== undefined) {
      drawToolInstance.setGeojson(geoJSON)
    }
  }

  public onDrawStart (onDrawStartCallback: IMapDrawCallback): void {
    this.on('drawstart', onDrawStartCallback)
    /* if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.onStart(this.onDrawStartCallback)
    } */
  }

  public dispatchDrawStartEvent (shape: string): void {
    this.dispatchEventWithData('drawstart', { shape })
  }

  public onDrawComplete (onDrawCompleteCallback: IMapDrawCallback): void {
    this.on('drawcomplete', onDrawCompleteCallback)
    /* if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.onComplete(this.onDrawCompleteCallback)
    } */
  }

  public dispatchDrawCompleteEvent (geojson: GeoJSON.FeatureCollection): void {
    this.dispatchEventWithData('drawcomplete', { geojson })
  }

  public onDrawUpdate (onDrawUpdateCallback: IMapDrawCallback): void {
    this.addEventListener('drawupdate', onDrawUpdateCallback)
    /* if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.onUpdate()
    } */
  }

  public dispatchDrawUpdateEvent (geojson: GeoJSON.FeatureCollection): void {
    this.dispatchEventWithData('drawupdate', { geojson })
  }

  public onDrawProgress (onDrawProgressCallback: IMapDrawCallback): void {
    this.addEventListener('drawprogress', onDrawProgressCallback)
    /* if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.onUpdateProgress(this.onDrawProgressCallback)
    } */
  }

  public dispatchDrawProgressEvent (geojson: GeoJSON.FeatureCollection): void {
    this.dispatchEventWithData('drawprogress', { geojson })
  }

  public enableDraw (shape: EMapShape): void {
    if (this.toolInstances.draw === undefined) {
      this.initIDrawTool({ enabled: false })
    }
    if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.enableMapShape(shape)
    }
  }

  public disableDraw (shape: EMapShape): void {
    if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.disableMapShape(shape)
    }
  }

  public clearDrawings (): void {
    if (this.toolInstances.draw !== undefined) {
      this.toolInstances.draw.clearDrawings()
    }
  }

  protected initIDrawTool (ops: IDrawToolPropsOptions | undefined = { enabled: true }): void {
    if (this?.mapLibrary?.Tools?.Draw !== undefined) {
      const drawProps: IDrawToolProps = {
        ...ops,
        map: this
      }
      const DrawClass: any = this.mapLibrary.Tools.Draw as unknown as IDrawTool
      const newIDrawTool: IDrawTool = new DrawClass(drawProps)
      this.toolInstances.draw = newIDrawTool
    }
  }

  protected initTools (): void {
    if (this?.tools?.draw !== undefined) {
      this.initIDrawTool(this.tools.draw)
    }
  }

  protected initMap (): void {
    if (this.element != null) {
      if (this.mapElement == null) {
        this.mapElement = document.createElement('div')
        this.mapElement.classList.add(`${this.getMapLibraryName()}-map`)
        this.mapElement.style.height = '100%'
        this.element.appendChild(this.mapElement)
      }
      this.createMap().then(map => {
        this.map = map
        if (this.baseLayer != null) {
          this.setBaseLayer(this.baseLayer)
        } else if (this.baseLayerKey !== undefined) {
          this.setBaseLayerKey(this.baseLayerKey)
        }
        if (this.layers != null) {
          this.addLayers(this.layers)
        }
        this.initTools()
        this.dispatchEvent(new MapStateEvent('loaded', {
          data: this.getMapState()
        }))
        if (this.mapElement !== undefined) {
          this.mapElement.onmouseleave = () => {
            this.dispatchPointerMove()
          }
        }
      }).catch(e => {
        console.log('Error initializing map')
        console.log(e)
      })
      // this.map = await this.createMap()
    }
  }

  protected async createMap (): Promise<any> {
    console.log(`createMap not implemented for ${this.getMapLibraryName()}`)
    return null
  }

  public isDestroyed (): boolean {
    if (this.map !== undefined && this.mapElement !== undefined) {
      return false
    }
    return true
  }

  public destroy (): void {
    this.layers?.forEach(layer => {
      delete layer.layer
    })
    if (this.baseLayer != null) {
      delete this.baseLayer.layer
    }
    this.cleanupMap()
    if ((this.element != null) && (this.mapElement != null)) {
      this.element.removeChild(this.mapElement)
      delete this.mapElement
      delete this.map
    }
  }

  protected cleanupMap (): void {
    console.log(`cleanupMap not implemented for ${this.getMapLibraryName()}`)
  }
}

export default MapRoot

export {
  MapDrawEvent,
  MapStateEvent
}

export type {
  MapRoot as MapRootType
}
